import './registerServiceWorker'
import '@/lib/animation'
import '@mdi/font/css/materialdesignicons.css'
import '@fontsource/roboto/100.css'
import '@fontsource/roboto/300.css'
import '@fontsource/roboto/400.css'
import '@fontsource/roboto/500.css'
import '@fontsource/roboto/700.css'
import '@fontsource/roboto/900.css'
import '@/styles/typography.scss'

import { createGtm } from '@gtm-support/vue-gtm'
import * as Sentry from '@sentry/vue'
import { DefaultApolloClient } from '@vue/apollo-composable'
import { createPinia } from 'pinia'
import { createApp, h, provide } from 'vue'

import { masterGuard } from '@/guards/masterGuard.js'
import { getAvvGuard } from '@/lib/avvGuard'
import { getCookieConsent } from '@/lib/cookieHandling'
import bus, { eventNames } from '@/lib/eventBus'
import { setApolloClient, syncHeapTrackingConsent } from '@/lib/heap'
import { getLicenseAgreementGuard } from '@/lib/licenseAgreementGuard'
import { getLegalConsentsGuard } from '@/lib/legalConsentsGuard'
import { getSignUpGuard } from '@/lib/signUpGuard'
import { FeaturePlugin } from '@/plugins/feature-config/'

import App from './App.vue'
import { AuthPlugin } from './auth'
// import CookieConsentPlugin from './modules/cookieConsent/plugin'
import { createAuth0 } from './auth/auth0'
import { onofficeAuth } from './auth/onoffice'
import ResponsiveImage from './components/ResponsiveImage.vue'
import i18n from './i18n'
import { hasCompanyGuard } from './lib/companyGuard'
import GlobalMixin from './mixins/global'
import TrackingPlugin from './plugins/tracking'
import UtmPlugin from './plugins/utm'
import vuetify from './plugins/vuetify'
import createRouter from './router'
import { createProvider } from './vue-apollo'

//const app = createApp(App)
const app = createApp({
  setup() {
    const apolloProvider = createProvider()
    // Provide Apollo client globally
    provide(DefaultApolloClient, apolloProvider.defaultClient)
  },
  render: () => h(App),
})

// Register global components
app.component('r-img', ResponsiveImage)

// Initialize Apollo Provider
const apolloProvider = createProvider().install(app)

// Set Apollo client for Heap.io identity enrichment
setApolloClient(apolloProvider.defaultClient)
syncHeapTrackingConsent(getCookieConsent().measurement)

// Create Router after Apollo is initialized
const router = createRouter(apolloProvider)

// Rest of plugins that don't depend on Apollo
// app.use(CookieConsentPlugin)
app.use(TrackingPlugin, { apolloProvider })
app.use(UtmPlugin)
app.use(GlobalMixin)

// Auth0 Configuration - needs to be before Feature Plugin
const auth0Plugin = createAuth0({
  domain: import.meta.env.VITE_APP_AUTH0_DOMAIN,
  clientId: import.meta.env.VITE_APP_AUTH0_CLIENT_ID,
  audience: import.meta.env.VITE_APP_AUTH0_AUDIENCE,
  databaseConnection: import.meta.env.VITE_APP_AUTH0_DATABASE_CONNECTION,
  redirectUri: window.location.origin + '/auth/callback',
  //@ts-expect-error: We have to move auth0.js to typescript to support types
  onRedirectCallback: (targetPath) => {
    router.push({
      ...(targetPath ? { path: targetPath } : { name: 'dashboard' }),
      replace: true,
    })
  },
})

app.use(AuthPlugin, {
  auth0: auth0Plugin,
  onofficeAuth,
})

// Feature Plugin Configuration
app.use(FeaturePlugin, {
  bus,
  apolloProvider,
  onCreated() {
    bus.$on(eventNames.USER_LOGGED_IN, (companyId) => {
      if (companyId) {
        this.loadAllFeaturesForCompany(companyId, { interval: 900000 })
      }
    })
  },
})

// GTM Configuration
if (import.meta.env.VITE_APP_GTM_ID) {
  try {
    const { measurement } = getCookieConsent()
    const gtmInstance = createGtm({
      id: import.meta.env.VITE_APP_GTM_ID,
      enabled: measurement,
      loadScript: true,
      vueRouter: router,
      debug: import.meta.env.MODE === 'development',
    })

    app.use(gtmInstance)
  } catch (error) {
    console.error('Error initializing GTM:', error)
  }
}
// Sentry Configuration
Sentry.init({
  app,
  attachProps: true,
  dsn: import.meta.env.VITE_APP_SENTRY_DSN,
  environment: import.meta.env.VITE_APP_STAGE,
  enabled: import.meta.env.VITE_APP_STAGE && import.meta.env.VITE_APP_STAGE !== 'local',
})

// Router Guards
router.beforeEach(hasCompanyGuard())
router.beforeEach(getSignUpGuard(apolloProvider))
router.beforeEach(getLicenseAgreementGuard(apolloProvider))
router.beforeEach(getLegalConsentsGuard(apolloProvider))
router.beforeEach(getAvvGuard(apolloProvider))
router.beforeEach(masterGuard(apolloProvider))

await i18n.syncRouteOverrides(router.currentRoute.value.path)
router.afterEach(async (to) => {
  await i18n.syncRouteOverrides(to.path)
})

// Mount Application
app.use(createPinia()).use(router).use(i18n.getInstance()).use(vuetify).mount('#app')

// Initialize tracking after app is mounted
router.initializeTracking(app)
